import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import {
  ScanFace, Search, Loader2, Download, RefreshCw, Clock, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FaceAttendanceLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('employee_code', search);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const { data } = await axios.get(`${API_URL}/attendance/face-logs?${params}`, { withCredentials: true });
      setLogs(data);
    } catch (e) { console.error(e); setLogs([]); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);


  return (
    <MainLayout>
      <div className="space-y-6" data-testid="face-attendance-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <ScanFace className="inline-block w-8 h-8 mr-2 mb-1" /> Face Recognition Attendance
            </h1>
            <p className="mt-1 text-sm text-slate-600">View attendance logs from face recognition devices</p>
          </div>
          <Button variant="outline" data-testid="face-logs-export-btn" onClick={() => {
            if (logs.length === 0) { toast.error('No logs to export'); return; }
            downloadCSV(logs.map(l => ({
              Employee: l.employee_code, Time: l.punch_time, Type: l.punch_type,
              Device: l.device_name, Status: l.processed ? 'Processed' : 'Pending',
            })), 'face_attendance_logs.csv');
            toast.success('Logs exported');
          }}>
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800">
          <strong>Face Recognition Attendance:</strong> This page displays all attendance records captured via face recognition devices.
          Logs are automatically pushed by registered devices. Records marked as "Pending" have not yet been processed into the attendance register.
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input data-testid="face-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee code..." className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm" />
          </div>
          <input type="date" data-testid="face-date-from" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm" />
          <input type="date" data-testid="face-date-to" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm" />
          <Button variant="outline" onClick={fetchLogs} data-testid="face-filter-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Filter
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total Logs</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono" data-testid="face-total-count">{logs.length}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-green-500">Processed</p>
            <p className="text-2xl font-semibold text-green-700 mt-1 font-mono">{logs.filter(l => l.processed).length}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-amber-500">Pending</p>
            <p className="text-2xl font-semibold text-amber-700 mt-1 font-mono">{logs.filter(l => !l.processed).length}</p>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="face-logs-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Employee Code</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Punch Time</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Device</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Confidence</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                  <ScanFace className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No face recognition logs found</p>
                  <p className="text-xs mt-1">Logs will appear when face recognition devices push attendance data</p>
                </td></tr>
              ) : logs.map((log, i) => (
                <tr key={log.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`face-log-row-${i}`}>
                  <td className="py-3 px-4 text-sm font-mono font-medium text-slate-900">{log.employee_code}</td>
                  <td className="py-3 px-4 text-sm text-slate-700 font-mono">{log.punch_time}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.punch_type === 'in' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {log.punch_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">{log.device_name || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm font-mono text-slate-600">{log.raw_data?.confidence ? `${(log.raw_data.confidence * 100).toFixed(1)}%` : 'N/A'}</td>
                  <td className="py-3 px-4 text-sm">
                    {log.processed ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle2 className="w-3 h-3" /> Processed</span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600 text-xs"><Clock className="w-3 h-3" /> Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </MainLayout>
  );
};

export default FaceAttendanceLogs;
